import type {
  MetafieldDimension,
  MetafieldVolume,
  MetafieldWeight,
} from '@shared/shop-api/core/types'
import type { MetafieldFragment } from '@shared/types/storefront/graphql'

export const getMetafieldValue = <TDefaultValue>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue?: TDefaultValue
): unknown | TDefaultValue => {
  const metafield = metafields.find((metafield) => metafield?.key === key)

  if (metafield?.type === 'url') {
    return metafield.value
  }

  if (metafield?.type === 'collection_reference') {
    return metafield.value
  }

  if (metafield?.type === 'multi_line_text_field') {
    return metafield.value
  }

  if (metafield?.type === 'single_line_text_field') {
    return metafield.value
  }

  if (metafield?.type === 'list.single_line_text_field') {
    return JSON.parse(metafield.value)
  }

  if (metafield?.type === 'boolean') {
    return metafield.value === 'true'
  }

  if (metafield?.type === 'date_time') {
    const date = new Date(metafield.value)

    if (!isNaN(date.getTime())) {
      return date
    }
  }

  if (metafield?.type === 'dimension') {
    return (JSON.parse(metafield.value) as MetafieldDimension).value
  }

  if (metafield?.type === 'weight') {
    return (JSON.parse(metafield.value) as MetafieldWeight).value
  }

  if (metafield?.type === 'volume') {
    return (JSON.parse(metafield.value) as MetafieldVolume).value
  }

  if (metafield?.type === 'json') {
    return JSON.parse(metafield.value) as unknown
  }

  return defaultValue
}

export const getMetafieldString = <TMetafieldType extends string = string>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue?: TMetafieldType
): TMetafieldType | undefined => {
  return getMetafieldValue(metafields, key, defaultValue) as TMetafieldType
}

export const getMetafieldList = <TMetafieldType extends string[] = string[]>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue: TMetafieldType = [] as unknown as TMetafieldType
): TMetafieldType => {
  return getMetafieldValue(metafields, key, defaultValue) as TMetafieldType
}

export const getMetafieldDimension = <TMetafieldType extends number = number>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue?: TMetafieldType
): TMetafieldType | undefined => {
  return getMetafieldValue(metafields, key, defaultValue) as TMetafieldType
}

export const getMetafieldBoolean = <TMetafieldType extends boolean = boolean>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue: TMetafieldType | false = false
): TMetafieldType | false => {
  return getMetafieldValue(metafields, key, defaultValue) as TMetafieldType
}

export const getMetafieldDateTime = <TMetafieldType extends Date = Date>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue?: TMetafieldType
): TMetafieldType | undefined => {
  return getMetafieldValue(metafields, key, defaultValue) as TMetafieldType
}

export const getMetafieldVolume = <TMetafieldType extends number = number>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue?: TMetafieldType
): TMetafieldType | undefined => {
  return getMetafieldValue(metafields, key, defaultValue) as TMetafieldType
}

export const getMetafieldWeight = <TMetafieldType extends number = number>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue?: TMetafieldType
): TMetafieldType | undefined => {
  return getMetafieldValue(metafields, key, defaultValue) as TMetafieldType
}

export const getMetafieldJson = <TMetafieldType>(
  metafields: (MetafieldFragment | null)[],
  key: string,
  defaultValue?: TMetafieldType
): TMetafieldType => {
  return getMetafieldValue(metafields, key, defaultValue) as TMetafieldType
}
